<template>
  <div class="Mall">
    <header class="mall-global-header">
      <!-- 1. 精简顶栏：极简快捷服务工具条 (完全保留原版功能) -->
      <div class="top-bar">
        <div class="wide-container bar-container">
          <div class="bar-left">
            <span class="welcome-text">
              您好，欢迎 <strong class="user-highlight">{{ clientName || '协议客户' }}</strong> 来到科普特
            </span>
            <span class="protocol-badge">协议客户专属通道</span>
          </div>
          <div class="bar-right">
            <a class="bar-link" @click="navTo('/mall/personal/myOrder')">我的订单</a>
            <a class="bar-link" @click="navTo('/mall/regularPurchaseList')">常购清单</a>
            <a class="bar-link" @click="navTo('/mall/personal/myAddress')">个人中心</a>
            <span class="bar-divider">|</span>
            <div class="user-company" v-if="clientName">
              <i class="el-icon-office-building company-icon"></i>
              <span>{{ clientName }}</span>
            </div>
            <a class="bar-link paas-link" @click="handleToPaas" v-if="clientToken">跳转到后台</a>
            <a class="bar-link logout-link" @click="logout" v-if="clientToken">退出登录</a>
            <a class="bar-link login-link" @click="navTo('/login')" v-else>请登录</a>
          </div>
        </div>
      </div>

      <!-- 2. 主导航条：真实的科普特官方生产 Logo + 极简药丸搜索 + 采购清单与常购清单按钮 -->
      <div class="main-header">
        <div class="wide-container header-container">
          <!-- 官方生产环境真实 Logo 图片 (一字不改使用原版 logo.jpg) -->
          <div class="brand-logo" @click="navTo('/mall')" title="杭州科普特电子有限公司">
            <img
              src="../../assets/img/logo.jpg"
              class="official-logo-img"
              alt="杭州科普特电子有限公司"
            />
          </div>

          <!-- 宽屏居中极简搜索条 (完全兼容现有文本搜索接口) -->
          <div class="search-box">
            <div class="search-input-wrap">
              <i class="el-icon-search search-icon"></i>
              <input
                type="text"
                v-model="searchText"
                placeholder="输入物料名称、型号或原厂 SKU 订货号搜索..."
                @keyup.enter="searchConfirm"
                class="search-input"
              />
              <button class="search-btn" @click="searchConfirm" type="button">
                <span>搜索</span>
              </button>
            </div>
          </div>

          <!-- 右侧操作区：采购清单(购物车) + 常购清单入口 (100% 对齐原版功能) -->
          <div class="header-right">
            <button class="action-pill-btn cart-btn" @click="navTo('/mall/cart')" type="button" title="购物车">
              <i class="el-icon-shopping-cart-2 action-icon"></i>
              <span class="action-label">购物车</span>
              <span class="cart-count-badge" v-if="cartNum > 0">
                {{ cartNum > 99 ? '99+' : cartNum }}
              </span>
            </button>
            <button class="action-pill-btn regular-btn" @click="navTo('/mall/regularPurchaseList')" type="button" title="常购清单">
              <i class="el-icon-collection-tag action-icon"></i>
              <span class="action-label">常购清单</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 3. 清爽横向分类导航条 (分类Popover + typeList动态循环 + 官方企业名与对账标识) -->
      <div class="category-nav-bar">
        <div class="wide-container nav-container">
          <div class="nav-left">
            <div class="category-trigger-wrap">
              <Category :hierarchyList="hierarchyList" />
            </div>
            <nav class="nav-menu">
              <a
                v-for="item in typeList"
                :key="'type' + item.id"
                :class="['nav-link', { active: judgeCurPath(item.id) }]"
                @click="selectType(item.id)"
              >
                {{ item.name }}
              </a>
            </nav>
          </div>
          <div class="nav-right">
            <div class="company-indicator">
              <span class="company-name">杭州科普特电子有限公司</span>
              <span class="divider">|</span>
              <span class="erp-status">
                <span class="status-dot"></span>
                <span>已联通企业 ERP 自动对账</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- 主体内容承载区 (包含面包屑与路由视口) -->
    <div
      class="content"
      :class="{
        'bg-white-page': [
          'Cart',
          'GoodsDetail',
          'CreateOrder',
          'OrderDetail',
          'RegularPurchaseList'
        ].includes(route)
      }"
    >
      <div class="wide-container content-container" :style="{ minHeight: clientHeight - 320 + 'px' }">
        <el-breadcrumb v-if="breadcrumbs.length" separator=">" class="breadcrumb">
          <el-breadcrumb-item v-for="item in breadcrumbs" :key="item.title">
            <a v-if="item.to" @click.prevent="goToBreadcrumb(item.to)">{{ item.title }}</a>
            <span v-else>{{ item.title }}</span>
          </el-breadcrumb-item>
        </el-breadcrumb>
        <router-view :key="key"></router-view>
      </div>
    </div>

    <!-- 4. 企业服务保障底栏 (5项企业采购保障，纯本地精简矢量 SVG 渲染) -->
    <section class="trust-guarantee-section">
      <div class="wide-container guarantee-grid">
        <div class="guarantee-card">
          <div class="icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="m9 12 2 2 4-4"/>
            </svg>
          </div>
          <div class="text-wrap">
            <div class="title">官方正品行货</div>
            <div class="desc">原厂授权 资质完备</div>
          </div>
        </div>
        <div class="guarantee-card">
          <div class="icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </div>
          <div class="text-wrap">
            <div class="title">15天无忧退换</div>
            <div class="desc">阳光采购 售后省心</div>
          </div>
        </div>
        <div class="guarantee-card">
          <div class="icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
            </svg>
          </div>
          <div class="text-wrap">
            <div class="title">专属客户经理</div>
            <div class="desc">1对1 免费技术响应</div>
          </div>
        </div>
        <div class="guarantee-card">
          <div class="icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="1" y="3" width="15" height="13"/>
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
              <circle cx="5.5" cy="18.5" r="2.5"/>
              <circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
          </div>
          <div class="text-wrap">
            <div class="title">协议客户全免邮</div>
            <div class="desc">多仓直发 准时到达</div>
          </div>
        </div>
        <div class="guarantee-card">
          <div class="icon-wrap">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1z"/>
              <line x1="8" y1="6" x2="16" y2="6"/>
              <line x1="8" y1="10" x2="16" y2="10"/>
              <line x1="8" y1="14" x2="12" y2="14"/>
            </svg>
          </div>
          <div class="text-wrap">
            <div class="title">13% 增值税专票</div>
            <div class="desc">企业月结 合规对账</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. 极简高阶企业 Footer (100% 对齐原版版权与热线信息) -->
    <footer class="mall-global-footer">
      <div class="wide-container footer-container">
        <div class="footer-left">
          <span>Copyright © 2004 杭州科普特电子有限公司 All Rights Reserved. 版权所有</span>
          <a class="beian-link" href="https://beian.miit.gov.cn" target="_blank">浙ICP备2024060952号-1</a>
        </div>
        <div class="footer-right">
          <span>服务热线：<strong class="phone-num">400-816-5908</strong></span>
          <span class="sep">|</span>
          <span>投诉监督：<strong class="phone-num">137-5822-4489</strong></span>
          <span class="sep">|</span>
          <span>工作日 08:30 - 18:00</span>
        </div>
      </div>
    </footer>

    <!-- 6. 右下角专属顾问悬浮挂件 (根据真实数据受控渲染) -->
    <ContractContactFloat :contact="contractContact" />
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import NoticeList from "../../components/NoticeList";
import {
  getClientSize,
  backToTop,
  otherSystemGetLoginToMeUrl,
} from "@/util/util";
import TipsInput from "../../components/TipsInput";
import Category from "@/components/Category";
import ContractContactFloat from "@/components/ContractContactFloat";
import { online } from "@/api/platform";
const { buildBreadcrumbs } = require("@/util/breadcrumb");
const { buildCategoryTree } = require("@/util/categoryTree");
const { buildPersonnelRecordQuery, getHospitalId } = require("@/util/personnelRecordQuery");
const { buildActiveContractQuery, normalizeContractContact } = require("@/util/contractContact");
const { navigateOrRefresh } = require("@/util/routerNavigation");

export default {
  name: "Mall",
  computed: {
    ...mapState(["clientToken", "clientName"]),
    route () {
      return this.$route.name;
    },
    curPath () {
      return this.$route.path;
    },
    cartNum () {
      return this.$store.state.cartNum;
    },
    breadcrumbs () {
      return buildBreadcrumbs(this.$route.matched);
    },
  },
  components: {
    NoticeList,
    TipsInput,
    Category,
    ContractContactFloat,
  },
  data () {
    return {
      clientHeight: getClientSize().height,
      shouldShowBT: false,
      searchText: "",
      typeList: [
        { id: -1, name: "首页" },
        { id: 1, name: "全部商品" },
      ],
      hierarchyList: [],
      contractContact: null,
      key: '',
    };
  },
  mounted () {
    if (this.$route.query.searchText) {
      this.searchText = this.$route.query.searchText;
    }
    if (this.clientToken) {
      this.getHierarchyList();
      this.getContractContact();
      this.$store.dispatch("getCartQuantity");
    } else {
      this.$store.commit("setData", {
        cartNum: 0,
      });
    }
  },
  methods: {
    ...mapMutations({
      clientLogout: "CLIENT_LOGOUT",
    }),
    async getContractContact () {
      try {
        const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : {};
        if (!user.id) return;
        const { result: personnelRecords } = await online(
          buildPersonnelRecordQuery(user.id),
          { suppressErrorNotification: true }
        );
        const organizationId = getHospitalId(personnelRecords);
        if (!organizationId) return;
        const { result: contracts } = await online(buildActiveContractQuery(organizationId));
        this.contractContact = normalizeContractContact(contracts);
      } catch (error) {
        this.contractContact = null;
      }
    },
    async getHierarchyList () {
      const { result } = await online({
        form_head_uuid: "fe404228a1214875953fd8b7c7a3f4f9",
        page: 1,
        limit: 10000,
      });
      this.hierarchyList = buildCategoryTree(result);
    },
    judgeCurPath (typeId) {
      if (typeId === -1) {
        if (this.curPath.indexOf("/show/index") > -1) {
          return true;
        } else {
          return false;
        }
      } else {
        if (this.curPath.indexOf(`/show/goodsList/${typeId}`) > -1) {
          return true;
        } else {
          return false;
        }
      }
    },
    navTo (route) {
      return navigateOrRefresh(
        this.$router,
        this.$route.fullPath,
        route,
        () => {
          this.key = Math.random();
        }
      );
    },
    goToBreadcrumb (to) {
      if (to && to !== this.$route.path) {
        this.$router.push(to);
      }
    },
    logout () {
      localStorage.clear();
      this.navTo("/login");
    },
    backToTop () {
      backToTop();
    },
    searchTip (tip) {
      alert(tip);
    },
    searchConfirm () {
      if (this.searchText) {
        if (this.$route.query.searchText === this.searchText) {
          this.key = Math.random();
        } else {
          this.navTo(`/mall/show/goodsList/0/1?searchText=${this.searchText}`);
        }
      } else {
        this.navTo(`/mall/show/goodsList/1/all`);
      }
    },
    selectType (typeId) {
      if (typeId === -1) {
        this.navTo("/mall/show/index");
      } else {
        this.navTo("/mall/show/goodsList/" + typeId + "/all");
      }
    },
    handleToPaas () {
      otherSystemGetLoginToMeUrl(
        "a29fd212e51c11eeb53b0242ac150003",
        "dCOsnTCUsE2p5xVI"
      );
    },
  },
};
</script>

<style scoped lang="less">
@import '../../assets/css/var.less';

.Mall {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: @brandSurface;
  color: @brandTextDark;
  font-family: @brandFontFamily;

  .mall-global-header {
    width: 100%;
    background-color: #fff;
    border-bottom: 1px solid @brandBorderLight;
    position: sticky;
    top: 0;
    z-index: 40;
    box-shadow: @brandShadowSm;

    // 1. 精简顶栏
    .top-bar {
      background-color: rgba(248, 250, 252, 0.85);
      border-bottom: 1px solid @brandBorderLight;
      height: 32px;
      font-size: 12px;
      color: @brandTextMuted;

      .bar-container {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .bar-left {
        display: flex;
        align-items: center;
        gap: 12px;

        .welcome-text {
          color: @brandTextMuted;
          .user-highlight {
            color: @brandPrimary;
            font-weight: 600;
          }
        }

        .protocol-badge {
          display: inline-flex;
          align-items: center;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          background-color: @brandLight;
          color: @brandPrimary;
          border: 1px solid rgba(15, 82, 186, 0.15);
        }
      }

      .bar-right {
        display: flex;
        align-items: center;
        gap: 16px;

        .bar-link {
          color: @brandTextBase;
          cursor: pointer;
          transition: color 0.15s ease;

          &:hover {
            color: @brandContainer;
          }

          &.logout-link {
            color: #ef4444;
            &:hover {
              color: #dc2626;
            }
          }

          &.login-link {
            color: @brandContainer;
            font-weight: 600;
          }

          &.paas-link {
            color: @brandPrimary;
          }
        }

        .bar-divider {
          color: @brandBorder;
          user-select: none;
        }

        .user-company {
          display: flex;
          align-items: center;
          gap: 4px;
          color: @brandTextDark;
          font-weight: 500;

          .company-icon {
            color: @brandTextLight;
            font-size: 14px;
          }
        }
      }
    }

    // 2. 主头部行 (Logo + 搜索 + 采购清单/常购清单)
    .main-header {
      padding: 12px 0;

      .header-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
      }

      // 官方真实的 logo.jpg 图片
      .brand-logo {
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
        flex-shrink: 0;

        .official-logo-img {
          height: 60px;
          width: auto;
          max-width: 260px;
          object-fit: contain;
          display: block;
        }
      }

      .search-box {
        flex: 1;
        max-width: 620px;

        .search-input-wrap {
          display: flex;
          align-items: center;
          background-color: #f8fafc;
          border: 1px solid @brandBorder;
          border-radius: 12px;
          padding: 3px 4px 3px 12px;
          transition: all 0.2s ease;

          &:hover {
            border-color: #cbd5e1;
          }

          &:focus-within {
            border-color: @brandContainer;
            background-color: #ffffff;
            box-shadow: 0 0 0 3px rgba(15, 82, 186, 0.1);
          }

          .search-icon {
            color: @brandTextLight;
            font-size: 16px;
            margin-right: 8px;
            flex-shrink: 0;
          }

          .search-input {
            width: 100%;
            border: none;
            background: transparent;
            outline: none;
            font-size: 13px;
            color: @brandTextDark;

            &::placeholder {
              color: @brandTextLight;
            }
          }

          .search-btn {
            background-color: @brandContainer;
            color: #ffffff;
            border: none;
            border-radius: 8px;
            padding: 8px 18px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            flex-shrink: 0;
            transition: background-color 0.15s ease;

            &:hover {
              background-color: @brandPrimary;
            }
          }
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;

        .action-pill-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          background-color: #f8fafc;
          border: 1px solid @brandBorder;
          color: @brandTextDark;
          cursor: pointer;
          transition: all 0.15s ease;

          &:hover {
            background-color: @brandLight;
            border-color: rgba(15, 82, 186, 0.3);
            color: @brandContainer;
          }

          .action-icon {
            font-size: 17px;
            color: @brandContainer;
          }

          .action-label {
            font-size: 13px;
            font-weight: 500;
          }

          .cart-count-badge {
            padding: 2px 6px;
            border-radius: 9999px;
            background-color: @brandContainer;
            color: #ffffff;
            font-size: 11px;
            font-weight: 600;
            line-height: 1;
            margin-left: 2px;
          }
        }
      }
    }

    // 3. 一级分类导航栏
    .category-nav-bar {
      border-top: 1px solid @brandBorderLight;
      background-color: #ffffff;
      height: 44px;

      .nav-container {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .nav-left {
        display: flex;
        align-items: center;
        gap: 8px;

        .category-trigger-wrap {
          margin-right: 16px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 4px;

          .nav-link {
            padding: 6px 14px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 500;
            color: @brandTextBase;
            cursor: pointer;
            transition: all 0.15s ease;

            &:hover {
              color: @brandContainer;
              background-color: @brandLight;
            }

            &.active {
              color: @brandPrimary;
              font-weight: 600;
              background-color: @brandLight;
            }
          }
        }
      }

      .nav-right {
        .company-indicator {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 12px;
          color: @brandTextMuted;

          .company-name {
            color: @brandTextBase;
            font-weight: 500;
          }

          .divider {
            color: @brandBorder;
          }

          .erp-status {
            display: flex;
            align-items: center;
            gap: 6px;

            .status-dot {
              width: 7px;
              height: 7px;
              border-radius: 50%;
              background-color: @brandSuccess;
              box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
            }
          }
        }
      }
    }
  }

  // 主内容区域
  .content {
    flex: 1;
    width: 100%;
    background-color: @brandSurface;
    padding: 16px 0 24px;

    &.bg-white-page {
      background-color: #ffffff;
    }

    .content-container {
      width: 100%;
    }

    .breadcrumb {
      margin-bottom: 14px;
      font-size: 12px;

      /deep/ .el-breadcrumb__inner {
        color: @brandTextMuted;
        font-weight: normal;

        a {
          color: @brandTextMuted;
          font-weight: normal;
          &:hover {
            color: @brandContainer;
          }
        }
      }

      /deep/ .el-breadcrumb__item:last-child .el-breadcrumb__inner {
        color: @brandTextDark;
        font-weight: 500;
      }
    }
  }

  // 4. 企业服务保障底栏
  .trust-guarantee-section {
    background-color: #ffffff;
    border-top: 1px solid @brandBorderLight;
    border-bottom: 1px solid @brandBorderLight;
    padding: 24px 0;

    .guarantee-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 16px;

      .guarantee-card {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 6px 12px;

        .icon-wrap {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          background-color: @brandLight;
          border: 1px solid rgba(15, 82, 186, 0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          color: @brandContainer;
          flex-shrink: 0;
        }

        .text-wrap {
          .title {
            font-size: 13px;
            font-weight: 600;
            color: @brandTextDark;
            line-height: 1.3;
          }

          .desc {
            font-size: 11px;
            color: @brandTextLight;
            margin-top: 2px;
          }
        }
      }
    }
  }

  // 5. 极简高阶企业 Footer
  .mall-global-footer {
    background-color: #f8fafc;
    border-top: 1px solid @brandBorder;
    font-size: 12px;
    color: @brandTextMuted;
    padding: 20px 0;

    .footer-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .footer-left {
      display: flex;
      align-items: center;
      gap: 16px;

      .beian-link {
        color: @brandTextMuted;
        transition: color 0.15s ease;

        &:hover {
          color: @brandContainer;
        }
      }
    }

    .footer-right {
      display: flex;
      align-items: center;
      gap: 12px;

      .phone-num {
        color: @brandTextDark;
        font-weight: 600;
      }

      .sep {
        color: @brandBorder;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/client/Mall.vue